import * as THREE from 'three';
import { POLYGONS, TREES, TREESPHERES, VARS } from "../Global/variables";
import SegmentManager from "../Segments/SegmentManager";
import ObstacleManager from "../Obstacles/ObstacleManager";
import { COLORS } from "../Global/materials";
import PanelManager from "../Panels/PanelManager";
import { createSegmentPlane } from "../Global/functions";
import { updateAvaArea, updateSetbackUI } from '../Segments/SetBacks/Functions';
import { getOrCreateCache, IRR_CACHE, markIrradianceClean } from "../../helpers/IrradianceCache";
import { createTrimSphereMesh } from "../Trees/treeDrawingUtils";
import { createTreeMesh } from "../Trees/treeGeometry";
import { deleteAllSegments } from "../Segments/Delete";
import { restoreRulers } from "../Ruler/RulerStore";

function createTreeFromSavedData(data) {
    const tree = createTreeMesh(data?.treeType || data?.shape);
    const trunk = tree.userData.trunk;
    const foliage = tree.userData.foliage;
    const arrow = tree.userData.arrow;
    tree.userData.locked = !!data?.locked;
    tree.userData.groundZ = 0;

    const groupScale = data?.groupScale || {};
    tree.scale.set(
        typeof groupScale.x === "number" ? groupScale.x : 1,
        typeof groupScale.y === "number" ? groupScale.y : 1,
        typeof groupScale.z === "number" ? groupScale.z : 1
    );

    const trunkScale = data?.trunkScale || {};
    trunk.scale.set(
        typeof trunkScale.x === "number" ? trunkScale.x : 1,
        typeof trunkScale.y === "number" ? trunkScale.y : 1,
        typeof trunkScale.z === "number" ? trunkScale.z : 1
    );

    const trunkPosition = data?.trunkPosition || {};
    trunk.position.set(
        typeof trunkPosition.x === "number" ? trunkPosition.x : trunk.position.x,
        typeof trunkPosition.y === "number" ? trunkPosition.y : trunk.position.y,
        typeof trunkPosition.z === "number" ? trunkPosition.z : trunk.position.z
    );

    const foliageScale = data?.foliageScale || {};
    foliage.scale.set(
        typeof foliageScale.x === "number" ? foliageScale.x : 1,
        typeof foliageScale.y === "number" ? foliageScale.y : 1,
        typeof foliageScale.z === "number" ? foliageScale.z : 1
    );

    const foliagePosition = data?.foliagePosition || {};
    foliage.position.set(
        typeof foliagePosition.x === "number" ? foliagePosition.x : foliage.position.x,
        typeof foliagePosition.y === "number" ? foliagePosition.y : foliage.position.y,
        typeof foliagePosition.z === "number" ? foliagePosition.z : foliage.position.z
    );

    const arrowPosition = data?.arrowPosition || {};
    arrow.position.set(
        typeof arrowPosition.x === "number" ? arrowPosition.x : arrow.position.x,
        typeof arrowPosition.y === "number" ? arrowPosition.y : arrow.position.y,
        typeof arrowPosition.z === "number" ? arrowPosition.z : arrow.position.z
    );

    const position = data?.position || {};
    tree.position.set(
        typeof position.x === "number" ? position.x : 0,
        typeof position.y === "number" ? position.y : 0,
        0
    );
    tree.userData.widthScale = Number(data?.widthScale ?? tree.scale.x);
    tree.userData.lengthScale = Number(data?.lengthScale ?? tree.scale.z);
    tree.userData.heightScaleY = Number(data?.heightScaleY ?? trunk.scale.y);

    return tree;
}

export function loadDrawings(
    scene,
    camera,
    updateSegmentList,
    updateObstacleList,
    updatePanelList,
    panelList,
    addSetback,
    setInputValues,
    setAvailableArea,
    setRemovedSections
) {
    if (VARS.Current_Project?.model_data && Object.keys(VARS.Current_Project.model_data).length !== 0) {
        if (typeof window !== "undefined") {
            window.__suppressIrradianceInvalidation = true;
        }
        try {
        // Replace the current scene/model state on reload instead of appending
        // restored geometry on top of what is already mounted.
        deleteAllSegments();
        if (typeof updateSegmentList === "function") {
            updateSegmentList([]);
        }
        if (typeof setRemovedSections === "function") {
            setRemovedSections((prev) => ({
                ...prev,
                spheres: [],
                trimmedSections: [],
            }));
        }

        (TREESPHERES?.spheres || []).forEach((sphere) => {
            if (sphere?.parent) sphere.parent.remove(sphere);
        });
        TREESPHERES.spheres = [];

        let parsed = JSON.parse(VARS.Current_Project.model_data);

        // ✅ NEW: support new save format with lidar
        let data = Array.isArray(parsed) ? parsed : parsed.polygons;
        let lidarPosition = parsed?.lidar?.position || null;
        const savedTrees = Array.isArray(parsed?.trees) ? parsed.trees : [];
        const savedTrimSpheres = Array.isArray(parsed?.trimSpheres) ? parsed.trimSpheres : [];
        const savedRulers = Array.isArray(parsed?.rulers) ? parsed.rulers : [];

        // Do NOT restore the saved LiDAR position here. buildLidarMesh() owns that
        // restore: it knows whether the payload is anchor-relative or world-space and
        // it also moves the arrow control. Re-enabling the block below would apply an
        // anchor-offset payload as an absolute position and leave the arrow behind.
        // if (lidarPosition) {
        //     const lidarMesh = scene.getObjectByName("lidarTerrainMesh");
        //     if (lidarMesh) {
        //         lidarMesh.position.set(
        //             lidarPosition.x,
        //             lidarPosition.y,
        //             lidarPosition.z
        //         );
        //     }
        // }

        // Restore saved ruler measurements
        restoreRulers(scene, savedRulers);

        // Restore trees
        (TREES?.trees || []).forEach((tree) => {
            if (tree?.parent) tree.parent.remove(tree);
        });
        TREES.trees = [];
        if (VARS?.selectedTree) {
            VARS.selectedTree = null;
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: null } }));
            }
        }

        savedTrees.forEach((treeData) => {
            const tree = createTreeFromSavedData(treeData);
            scene.add(tree);
            TREES.trees.push(tree);
        });

        // Restore exclusion spheres (irradiance-only cutout volumes)
        const restoredSpheres = [];
        savedTrimSpheres.forEach((config) => {
            const sphere = createTrimSphereMesh({
                position: config?.position,
                scale: config?.scale,
                locked: !!config?.locked,
                enabled: config?.enabled !== false,
            });
            scene.add(sphere);
            TREESPHERES.spheres.push(sphere);
            restoredSpheres.push(sphere);
        });

        if (typeof setRemovedSections === "function") {
            setRemovedSections((prev) => ({
                ...prev,
                spheres: restoredSpheres,
                trimmedSections: [],
            }));
        }

        for (let i = 0; i < data.length; i++) {
            let coordinates = data[i].polygon;

            let segment = new SegmentManager(scene, null, updateSegmentList);
            segment.addShape(coordinates);

            if (data[i].irradiance) {
                const savedPoints = Array.isArray(data[i].irradiance.irradiancePoints)
                    ? data[i].irradiance.irradiancePoints.map(pt => ({
                        pointIndex: pt.pointIndex ?? null,
                        coordinates: pt.coordinates
                            ? { x: pt.coordinates.x, y: pt.coordinates.y, z: pt.coordinates.z }
                            : null,
                        totalIrradiance: pt.totalIrradiance ?? null,
                        totalIrradianceUnshaded: pt.totalIrradianceUnshaded ?? null,
                        asa: pt.asa ?? null
                    }))
                    : [];

                const savedMonthly = Array.isArray(data[i].irradiance.monthlyASA)
                    ? data[i].irradiance.monthlyASA.map(m => ({
                        month: m.month,
                        asa: m.asa,
                    }))
                    : [];

                POLYGONS[i].irradiance = {
                    ASA: data[i].irradiance.ASA ?? null,
                    TOF: data[i].irradiance.TOF ?? null,
                    monthlyASA: savedMonthly,
                    irradiancePoints: savedPoints,
                };

                // Mirror into IRR_CACHE so we can skip recompute and show sprites immediately
                const segId = POLYGONS[i]?.id;
                if (segId) {
                    const cache = getOrCreateCache(segId);
                    const hasSavedIrradiance = Array.isArray(savedPoints) && savedPoints.length > 0;

                    if (cache) {
                        cache.tof = data[i].irradiance.TOF ?? null;
                        cache.asa = data[i].irradiance.ASA ?? null;
                        cache.monthlyASA = savedMonthly;
                        cache.irradiancePoints = savedPoints;
                        cache.displayPoints = hasSavedIrradiance
                            ? savedPoints.map((pt) => ({
                                pointIndex: pt.pointIndex ?? null,
                                coordinates: pt.coordinates || { x: 0, y: 0, z: 0 },
                                totalIrradiance: pt.totalIrradiance ?? null,
                                totalIrradianceUnshaded: pt.totalIrradianceUnshaded ?? null,
                                asa: pt.asa ?? null,
                            }))
                            : [];

                        if (hasSavedIrradiance) {
                            cache.status = {
                                ...(cache.status || {}),
                                asa: "done",
                                tof: cache.tof != null ? "done" : "done",
                                gti: "done", // skip background GTI precompute for saved irradiance
                            };
                            IRR_CACHE.set(segId, cache);
                            markIrradianceClean(segId);
                        } else {
                            IRR_CACHE.set(segId, cache);
                        }
                    }
                }
            }

            if (data[i].setbacks && data[i].setbacks.length > 0) {
                data[i].setbacks.forEach((sb, index) => {
                    updateSetbackUI(i, setInputValues, sb.value, index);
                });
                addSetback({ setbackAdded: true, segmentIndex: i });
                updateAvaArea(i, setAvailableArea);
            }

            if (data[i].obstruction && data[i].obstruction.length > 0) {
                data[i].obstruction.forEach((obs) => {
                    let obstacle = new ObstacleManager(scene, null, null, null, updateObstacleList, i);

                    if (obs.type === "rectangle") {
                        obstacle.completeShape(
                            obs.polygon,
                            obs.height,
                            obs.offset ? obs.offset : 2,
                            COLORS.red,
                            "rectangle",
                            obs.isFilled === true
                        );
                    }
                    if (obs.type === "circle") {
                        obstacle.completeShape(
                            obs.polygon,
                            obs.height,
                            obs.offset ? obs.offset : 2,
                            COLORS.red,
                            "circle",
                            obs.isFilled === true
                        );
                    }
                    if (obs.type === "polygon") {
                        obstacle.completeShape(
                            obs.polygon,
                            obs.height,
                            obs.offset ? obs.offset : 2,
                            COLORS.red,
                            "polygon",
                            obs.isFilled === true
                        );
                    }

                    obstacle = null;
                });
            }

            if (data[i].panels && data[i].panels.length > 0) {
                data[i].panels.forEach(async function (pan) {
                    try {
                        let solarPanel = {
                            id: pan.id,
                            height: pan.height,
                            tilt: pan.tilt,
                            rotation: pan.rotation,
                            azimuth: pan.azimuth,
                            orientation: pan.orientation
                        };

                        let panel = new PanelManager(
                            scene,
                            camera,
                            i,
                            solarPanel,
                            updatePanelList,
                            panelList,
                            null,
                            pan.position,
                            null
                        );

                        let plane = createSegmentPlane(i);

                        let position = new THREE.Vector3(
                            pan.position.x,
                            pan.position.y,
                            pan.position.z
                        );

                        let _ = await panel.createPanel(position, POLYGONS[i], plane, true);

                        panel.addPanel();

                        panel = null;
                    } catch (e) {
                        console.error("Error creating panel:", e);
                    }
                });
            }
            segment = null;
        }
        } finally {
            if (typeof window !== "undefined") {
                window.__suppressIrradianceInvalidation = false;
            }
        }
    }
}
